// =============================================================================
// selectors/leaks.ts — the Coach's Report's numbers, for `ui/`.
// -----------------------------------------------------------------------------
// ⚠ `ui/` may not import `domain/` at all.
//
// ── ⚠ THE ACCOUNT NARROWING IS DONE HERE, NOT INSIDE A LEAK ──
//
// Each leak measures whatever list it is handed. Which trades that list holds is
// a question about what the trader is looking at, and it belongs at the seam —
// the same division `selectors/goals.ts` draws. A leak that filtered by account
// itself would apply the selection twice the moment a caller pre-narrowed.
// =============================================================================

export {
  LEAK_METRICS, leakById, leakDollarsStand, leakReviewSet, leakSample, rankLeaks,
  realizedExpectancy, topLeak,
} from '@domain/leaks';

export type {
  LeakContext, LeakKind, LeakMetric, LeakRanking, LeakReviewColumn,
  LeakReviewSet, LeakSample, LeakTrade, RankedLeak,
} from '@domain/leaks';

// ── the lifecycle: Fix it, Prove it ──────────────────────────────────────────
export {
  ADHERENCE_MIN_MARKED, ADHERENCE_MIN_PCT, activeLeakRules, adherenceMark,
  baselineUsable, nextMark, proveRule, ruleAdherence,
} from '@domain/leakRules';

export type {
  Adherence, AdherenceTrade, LeakBaseline, LeakRule, ProveResult, Verdict,
} from '@domain/leakRules';

export {
  bankRule, canFixLeak, captureLeakBaseline, createRule, leakLedger,
  leakWindowOf, retireRule,
} from '@domain/leakLoop';

export type { LedgerRule, LoopWrite, RuleStatus } from '@domain/leakLoop';

export {
  COACH_WINDOWS, DEFAULT_COACH_WINDOW, coachPriorWindow, coachWindowPhrase,
  coachWindowStart, readSince, tradesInCoachWindow, tradesInSpan,
} from '@domain/coachWindow';

export type { CoachWindowId, CoachWindowSpan } from '@domain/coachWindow';

export {
  TREND_MIN_PRIOR_TRADES, coachHeadline, coachTrendRows,
} from '@domain/coachTrend';

export type { CoachHeadline, TrendRow, TrendUnit } from '@domain/coachTrend';

/**
 * Narrow to one account, or keep everything.
 *
 * ⚠ `null` MEANS EVERY ACCOUNT, and it is not the same as an empty string. The
 * picker's "All accounts" option carries an empty value, so the caller maps it
 * to null explicitly rather than letting a falsy check decide — an account whose
 * id happened to be empty would otherwise select the whole book.
 */
export function tradesForAccount<T extends { accountId: string | null }>(
  trades: readonly T[], accountId: string | null,
): readonly T[] {
  if (accountId === null) return trades;
  return trades.filter((t) => String(t.accountId ?? '') === accountId);
}

export interface MethodAccount {
  id: string;
  excludeFromMetrics?: boolean | undefined;
  archived?: boolean | undefined;
}

/**
 * The accounts the method may measure.
 *
 * ── 🚨 ONE SIGNAL ONLY: "EXCLUDE FROM METRICS". ARCHIVING IS NOT A FILTER ──
 *
 * ⚠ A DELIBERATE DIVERGENCE FROM THE OLD CLIENT (PD-11), on Josh's instruction:
 * *"Accounts that are archived should count. Accounts that are tagged 'Exclude
 * From Metrics' do not. It's an important distinction."*
 *
 * The old client's `countsForMethod` drops BOTH, and it is the only place in
 * either client that treats archiving as anything but de-clutter — its own
 * comment concedes "archived is otherwise visual-only". That reading is wrong
 * for the two reasons his book makes concrete:
 *
 *   1. Every one of his archived accounts has "exclude from metrics" explicitly
 *      OFF. He said, per account, that it should count. Archiving is how he
 *      tidies a blown account off the list, not how he hides it from the maths.
 *   2. It silently ORPHANS a live rule. The two rules he is testing right now
 *      were frozen on 2026-07-19 against 258 and 112 trades. He archived those
 *      accounts afterwards, and under the old rule the same report can now see
 *      TWELVE trades of his 716 — so both rules would read "not enough trades
 *      yet" forever, with nothing on screen connecting that to an archive button
 *      he pressed weeks ago on a different tab.
 *
 * ⚠ THE PRACTICE HALF IS UNCHANGED AND IS THE POINT. SIM, Replay and Demo are
 * tagged excluded and stay out — a baseline frozen over replay trades would be
 * re-proved against live ones, and the difference would read as an improvement
 * nobody made.
 */
export function methodAccountIds(
  accounts: readonly MethodAccount[] | null | undefined,
): ReadonlySet<string> {
  return new Set(
    (accounts ?? [])
      .filter((a) => !a.excludeFromMetrics)
      .map((a) => String(a.id)),
  );
}

/**
 * Trades the method may measure.
 *
 * ⚠ AN UNASSIGNED OR ORPHANED TRADE IS KEPT, matching the old client. It is a
 * real trade whose account is merely missing, and dropping it would quietly
 * shrink every leak for a trader mid-import. Only the ONE explicit user signal —
 * exclude from metrics — takes a trade out.
 */
export function tradesForMethod<T extends { accountId: string | null }>(
  trades: readonly T[],
  accounts: readonly MethodAccount[] | null | undefined,
): readonly T[] {
  const all = new Set((accounts ?? []).map((a) => String(a.id)));
  const allowed = methodAccountIds(accounts);
  return trades.filter((t) => {
    const id = String(t.accountId ?? '');
    if (!id || !all.has(id)) return true;
    return allowed.has(id);
  });
}
